// The "check for an update, then resolve it" sequence for an imported coach fork, shared by
// the two screens that apply it at the CANONICAL-DOCUMENT level — exercise-switch and
// exercise-stats. Neither has the exercise open for editing, so both write straight to the
// saved document with no draft pipeline in between:
//
//   re-check the source → short-circuit on deleted / unreachable / missing rev → build the
//   3-way diff → short-circuit on an empty diff (full-acknowledging the rev if the update
//   indicator is lit) → push the ExerciseDiffPage modal → apply, ignore, or do nothing.
//
// `edit-exercise-page` deliberately does NOT use this — it reaches the same outcome through
// the draft pipeline and differs in every branch.
//
// Why the class is shaped this way (a plain class rather than an `@Injectable`, separate
// from the DI-free `exercise-fork-update.ts`, and why the edit page stays out):
// `design-docs/public-exercises.md`, "The shared canonical-apply flow".

import { CustomExercise, ExerciseDbService } from './exercise-db.service';
import { ExerciseDiffEntry, ExerciseDiffField } from './exercise-diff';
import { buildLabeledCoachUpdateDiff } from './exercise-diff-ui';
import { applyDiffFieldsToCanonical, computeHasUpdate, resolveLocalCustomExerciseAsync } from './exercise-fork-update';
import { UserDb } from './user-db.service';
import { LoggingService } from './logging.service';
import { SettingsService } from './settings.service';
import { DatabaseWrapper, DocWatcher } from './database-wrapper';
import { NavController } from 'providers/nav-controller';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { ExerciseDiffResult, IExerciseDiffPage } from 'pages/exercise-diff/exercise-diff-page-interface';

// What one run of the flow is updating. The page builds this per invocation because the
// switch page checks whichever row's menu was opened, while the stats page always checks the
// one exercise it is showing.
export interface CoachForkUpdateTarget {
  // the imported fork whose source is being re-checked.
  fork: CustomExercise;
  // Runs after a successful save, with the identity-map canonical document. The page repoints
  // whatever it renders at that object and clears its own update indicator — a local write
  // never fires a DocWatcher (those run on replication pulls only), so nothing else will.
  onApplied(canonical: CustomExercise): void;
}

export interface CoachForkUpdateFlowOptions {
  // Names this screen in telemetry messages, e.g. 'exercise-switch'. Existing Sentry issues
  // are grouped by those strings, so it must keep matching what the page reported before.
  pageName: string;
  navCtrl: NavController;
  userDb: UserDb;
  exerciseDb: ExerciseDbService;
  toastController: ToastController;
  logging: LoggingService;
  settings: SettingsService;
  // The DocWatcher the page holds for its own reads. Read lazily because pages create it
  // during their load, after the flow itself is constructed.
  getDocWatcher(): DocWatcher;
  // True once the page's view is gone. Everything the flow does after an await is gated on
  // this, exactly as the page code was.
  isViewDestroyed(): boolean;
  // Display name for a `similarExerciseId`. Page-specific because the switch page can look the
  // id up in the exercise lists it has already loaded and the stats page has no such list.
  resolveSimilarExerciseName(id: string | undefined): string;
}

export class CoachForkUpdateFlow {
  // Context for the ExerciseDiffPage modal's `onDiffPickerResult` returnCallback — the page
  // hands back only the picked action, so the target/source/rev are stashed here across the
  // push. The callback name has to stay a method on the PAGE (`NavController` finds the
  // handler by searching the nav stack for a page instance carrying that method name), so
  // each page keeps a one-line method that hands over to `handlePickerResultAsync`.
  private pendingDiffApply: {
    target: CoachForkUpdateTarget;
    source: CustomExercise;
    sourceRev: string;
    diffLength: number;
  } | null = null;

  constructor(private readonly opts: CoachForkUpdateFlowOptions) { }

  // Resolves a fork's source from the user's OWN DB so a same-user local source isn't
  // reported as a deleted shared exercise (#3808). Passed into checkImportedExerciseForUpdateAsync.
  private resolveLocalSource = (id: string) => resolveLocalCustomExerciseAsync(this.opts.userDb, id);

  // "Update" / "Check for Update". Opens the diff picker IN PLACE (app-level modal) rather
  // than navigating to edit-exercise-page, then applies at the canonical-doc level. Short-
  // circuit branches first:
  //   - source deleted → `originalNoLongerAvailable` toast
  //   - source unresolved (offline) → `checkForUpdateOffline` toast
  //   - up to date AND nothing still differs → `alreadyUpToDateToast`
  //   - rev advanced but no user-visible diff → silent full-acknowledge, then that toast
  //   - otherwise → present the picker; apply / ignore / cancel per the R3 table
  //
  // Note `hasUpdate` can be false while fields still differ (partial-apply / ignore
  // re-entry); we diff against the just-fetched source directly, so the picker re-opens
  // on whatever still differs.
  async checkForUpdateAsync(target: CoachForkUpdateTarget): Promise<void> {
    const ex = target.fork;
    if (!ex?.local?.sourceExerciseId) return;
    const result = await this.opts.exerciseDb.checkImportedExerciseForUpdateAsync(
      ex, this.opts.getDocWatcher(), this.resolveLocalSource);
    if (this.opts.isViewDestroyed()) return;
    if (result.deleted) {
      this.showToast(LL.editExercise.toasts.originalNoLongerAvailable(), ToastType.WarningStrong, 3000);
      return;
    }
    if (!result.original) {
      // Swallowed network-error case (see exercise-db.service.ts) — be honest about the
      // offline state instead of misrepresenting it as "Already up to date."
      this.showToast(LL.editExercise.toasts.checkForUpdateOffline(), ToastType.WarningStrong, 3000);
      return;
    }
    const source = result.original;
    const sourceRev = source._rev;
    if (!sourceRev) {
      // a resolved source always has a _rev — a missing one is an impossible/malformed
      // state, so surface it rather than silently aborting the check.
      this.opts.logging.trackSilentProblem(
        `${this.opts.pageName}: resolved source missing _rev`, { docId: ex._id });
      return;
    }
    const diff = this.computeExerciseDiff(ex, source);
    if (diff.length === 0) {
      // Empty diff but a stale indicator may be lit: the staging rev advanced with no
      // user-visible (diffable) change — e.g. a metadata-only re-publish, or the only
      // fork-vs-source difference is the user's own divergence the 3-way diff suppresses.
      // The fork's content already matches the source on every diffable field, so
      // full-acknowledge the rev (bump both revs via isFullApply) to clear the "update
      // available" indicator. Without this, a metadata-only rev bump (or a rev whose only
      // difference is the user's own 3-way-suppressed divergence) would leave it stuck lit
      // with no way to dismiss it. Same rev-stamping as edit-exercise-page's empty-diff
      // `bumpLocalSourceRevisionAsync`, but applied silently here (the re-check is itself the
      // user action; there is nothing to pick) rather than behind that page's confirm alert.
      if (computeHasUpdate(ex.local, sourceRev)) {
        const ok = await this.applyToCanonicalAsync(target, source, new Set(), sourceRev, true);
        if (this.opts.isViewDestroyed()) return;
        if (!ok) return; // applier already surfaced the failure toast — don't claim success
      }
      this.showToast(LL.editExercise.alerts.updateAvailable.alreadyUpToDateToast(), ToastType.SuccessLight, 2000);
      return;
    }

    // present the per-field diff on the ExerciseDiffPage modal; it returns the user's choice
    // via the page's `onDiffPickerResult` returnCallback, which hands back here. stash what
    // that needs (the page only hands back the picked action, not the source/fork context).
    this.pendingDiffApply = { target, source, sourceRev, diffLength: diff.length };
    void this.opts.navCtrl.pushAsync(IExerciseDiffPage, {
      returnCallback: 'onDiffPickerResult',
      local: { diff },
    });
  }

  // The body of each page's `onDiffPickerResult` returnCallback. `result` is undefined when
  // the user backed out (header close / hardware-back) — a cancel: nothing is written, so the
  // genuine update stays unresolved (the "Update" affordance and the indicator remain).
  async handlePickerResultAsync(result?: ExerciseDiffResult): Promise<void> {
    const pending = this.pendingDiffApply;
    this.pendingDiffApply = null;
    if (!pending || !result || this.opts.isViewDestroyed()) return;
    const { target, source, sourceRev, diffLength } = pending;
    if (result.action === 'apply') {
      const appliedFields = result.appliedFields;
      const isFullApply = appliedFields.size === diffLength;
      const ok = await this.applyToCanonicalAsync(target, source, appliedFields, sourceRev, isFullApply);
      if (this.opts.isViewDestroyed()) return;
      // only claim success when the apply actually landed — the applier surfaces its own
      // failure toast, so a success toast on top would contradict it.
      if (ok) {
        this.showToast(
          isFullApply
            ? LL.editExercise.toasts.exerciseRefreshed()
            : LL.editExercise.toasts.partialUpdateApplied({ count: appliedFields.size }),
          ToastType.SuccessLight, 2000);
      }
    } else if (result.action === 'ignore') {
      // Explicit Ignore: mark the rev seen (lastSeenSourceRevision only) so the update
      // indicator clears and the fork reads as resolved (the ⋮ menu reverts to "Check for
      // Update"). sourceRevision untouched (lineage safety). No success toast here — a
      // failure surfaces via the applier's own refreshFailed toast.
      await this.applyToCanonicalAsync(target, source, new Set(), sourceRev, false);
    }
  }

  // Apply the picker result to the canonical custom-exercise doc (no draft pipeline — the
  // exercise isn't open for editing). Loads the identity-map canonical, mutates it via the
  // shared applier (field copy + R3 rev stamp), saves, then lets the page refresh itself.
  // Returns true when the apply landed, false when it caught a failure (and surfaced its own
  // toast) — callers gate their success toast on this so the two never contradict.
  private async applyToCanonicalAsync(
    target: CoachForkUpdateTarget,
    source: CustomExercise,
    appliedFields: Set<ExerciseDiffField>,
    sourceRev: string,
    isFullApply: boolean,
  ): Promise<boolean> {
    const docId = target.fork._id;
    try {
      // getCustomExerciseByIdAsync THROWS isPouchNotFound on a missing doc (never returns
      // null) — a doc deleted between menu render and apply lands in the catch below.
      const canonical = await this.opts.userDb.getCustomExerciseByIdAsync(docId);
      applyDiffFieldsToCanonical(canonical, source, appliedFields, sourceRev, isFullApply);
      await this.opts.userDb.saveCustomExerciseAsync(canonical, DatabaseWrapper.getTransactionId());
      if (this.opts.isViewDestroyed()) return true;
      target.onApplied(canonical);
      return true;
    } catch (err: any) {
      console.error(`${this.opts.pageName}: apply update to canonical failed`, err);
      this.opts.logging.trackSilentProblem(`${this.opts.pageName}: apply update to canonical failed`, {
        docId, err: String(err?.message ?? err),
      });
      this.showToast(LL.editExercise.toasts.refreshFailed(), ToastType.WarningStrong, 3000);
      return false;
    }
  }

  // Build the structured per-field diff for the picker via the shared `exercise-diff-ui`
  // helper. Only the similar-exercise-name lookup is page-specific; the equipment + i18n
  // wiring lives in the shared helper.
  private computeExerciseDiff(local: CustomExercise, source: CustomExercise): ExerciseDiffEntry[] {
    return buildLabeledCoachUpdateDiff(local, source, this.opts.settings.locale,
      id => this.opts.resolveSimilarExerciseName(id));
  }

  // show one of the flow's toasts; every branch above goes through here.
  private showToast(message: TranslatedString, type: ToastType, duration: number): void {
    this.opts.toastController.showToast({ message, type, duration });
  }
}
